
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import axios, { CancelTokenSource, CancelToken } from "axios";
import api from "@/services/api.service";

import NavHeader from "@/components/NavHeader.vue";
import { UserPermissions, FeaturesList, VueBootstrapField } from "@/store/types";
import { UserDetails, UserGroup } from "@/store/user-management/types";
import GenericUpdateModal, { ModalItem } from "@/components/table/generic-update-modal.vue";
import { debounce, get } from "lodash";
import DisableUser from "@/components/form/DisableUser.vue";
import { SiteTreeNode, Group } from "@/store/eventqueue/types";
import TextHighlight from "vue-text-highlight";
import UserGroupInput from "./UserGroupInput.vue";
import PasswordComplexity from "@/components/form/PasswordComplexity.vue";
import SureViewIcon from "@/components/SureViewIcon.vue";
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";

const Eventqueue = namespace("eventqueue");
const UserManagement = namespace("userManagement");
const GenericTableStore = namespace("GenericTable");
const License = namespace("license");
const Areas = namespace("areas");

const debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;

@Component({
	components: {
		"nav-header": NavHeader,
		"text-highlight": TextHighlight,
		"area-tree-select": AreaTreeSelect,
		"disable-user": DisableUser,
		"password-complexity": PasswordComplexity,
		"generic-update-modal": GenericUpdateModal,
		"sureview-icon": SureViewIcon,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	}
})
export default class UserManagementSetup extends Vue {
	$refs!: {
		GenericUpdateModal: GenericUpdateModal;
	};

	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getUserId") public currentUserId!: number;
	@Getter("getUserGroupId") public currentGroupId!: number;

	@Mutation private setIsLoading: (loading: boolean) => void;

	@UserManagement.State private pageIndex: number;
	@UserManagement.State private pageSize: number;
	@UserManagement.State private userTableIsBusy: boolean;
	@UserManagement.State private userList: UserDetails[];

	@UserManagement.Getter("getAllUsersRetrieved") private allUsersRetrieved: boolean;

	@UserManagement.Mutation private setPageIndex: (pageIndex: number) => void;
	@UserManagement.Mutation private resetUserList: () => void;
	@UserManagement.Mutation private resetState: () => void;
	@UserManagement.Mutation private setUserList: (users: UserDetails[]) => void;
	@UserManagement.Mutation private setAllUsersRetrieved: (allRetrieved: boolean) => void;
	@UserManagement.Mutation private setUserTableIsBusy: (isBusy: boolean) => void;

	@UserManagement.Action updateUser: (updatedUser: UserDetails) => Promise<any>;
	@UserManagement.Action deleteUser: (userId: number) => Promise<void>;
	@UserManagement.Action createUser: any;
	@UserManagement.Action getUserGroup: any;

	@Areas.State areaDictionary: { [id: number]: string };
	@Areas.Getter getAreaTitle: (id: number) => string;
	@Areas.Action fetchAreaDictionary: () => Promise<void>;

	@License.Action fetchLicenseDetails: () => Promise<void>;

	@GenericTableStore.Getter getModalRow: any;

	public permissionsList: UserGroup[] = [];

	public emailAsUser: boolean = false;
	private filter: string;
	private loadingMoreEntries: boolean = false;
	private searchTerm: string = "";
	public userSearchToken: CancelTokenSource = null;
	public editPermissions: string[];
	private includeBlankUsernames: boolean = false;

	private tableHeaders: VueBootstrapField[] = [
		{
			label: "UserID",
			key: "userID",
			sortDirection: "asc",
			headerTitle: "The users UserID",
			searchable: false,
			visible: false,
			dataType: "input",
			sortable: true
		},
		{
			label: !this.emailAsUser ? "Username" : "Email",
			key: "username",
			sortDirection: "asc",
			headerTitle: "The users username",
			searchable: true,
			visible: true,
			dataType: "input",
			sortable: true
		},
		{
			label: "Full Name",
			key: "fullName",
			sortDirection: "asc",
			headerTitle: "The users full name",
			searchable: true,
			visible: true,
			dataType: "input",
			sortable: true
		},
		{
			label: "Area",
			key: "groupName",
			sortDirection: "asc",
			headerTitle: "The area that the user belongs to",
			searchable: true,
			visible: true,
			dataType: "input",
			sortable: true
		},
		{
			label: "User Group",
			key: "permissionsGroup",
			sortDirection: "asc",
			headerTitle: "The permissions group the user is part of",
			searchable: true,
			visible: true,
			dataType: "list",
			sortable: true /*,
							useCustomCell: true*/
		},
		{
			label: "Email",
			key: "email",
			sortDirection: "asc",
			headerTitle: "The users email address",
			searchable: true,
			visible: !this.emailAsUser,
			dataType: "email",
			sortable: true
		},
		{
			label: "Telephone",
			key: "telephone",
			sortDirection: "asc",
			headerTitle: "The users phone numner",
			searchable: true,
			visible: true,
			dataType: "telephone",
			sortable: true
		},
		{
			label: "Cell",
			key: "mobile",
			sortDirection: "asc",
			headerTitle: "The users cell numner",
			searchable: true,
			visible: true,
			dataType: "telephone",
			sortable: true
		},
		{
			label: "Available as Contact",
			key: "isSiteStaff",
			sortDirection: "asc",
			headerTitle: "Is the user available as a contact",
			searchable: false,
			visible: true,
			dataType: "text",
			sortable: true /*,
							useCustomCell: true*/
		},
		{
			label: "Mobile Enabled",
			key: "mobileAppEnabled",
			sortDirection: "asc",
			headerTitle: "Does the user have mobile enabled",
			searchable: false,
			visible: true,
			dataType: "text",
			sortable: true /*,
							useCustomCell: true*/
		},
		{
			label: "User Enabled",
			key: "disabledAt",
			sortDirection: "asc",
			searchable: false,
			visible: true,
			dataType: "text",
			sortable: true /*,
							useCustomCell: true*/
		},
		{ key: "actions", label: "Actions", visible: true, sortable: false }
	];

	private getTableHeaders() {
		return this.tableHeaders.filter(h => h.visible);
	}

	private getModalItems(): ModalItem[] {
		return [
			{
				title: !this.emailAsUser ? "Username" : "Email",
				key: "username",
				dataType: "text",
				required: true,
				order: 1,
				maxLength: 150,
				minLength: 6
			},
			{
				title: "Password",
				key: "password",
				dataType: "component",
				linkedKey: "username",
				data: PasswordComplexity,
				readOnly: false,
				newOnly: true,
				visible: !this.isADChecked,
				required: !this.isADChecked,
				placeholder: "Password",
				order: 2,
				maxLength: 100
			},
			{
				title: "Use Active Directory",
				key: "useAD",
				visible: this.canEnableAD,
				dataType: "checkbox",
				readOnly: false,
				newOnly: true,
				required: false,
				order: 3
			},
			{
				title: "Full Name",
				key: "fullName",
				dataType: "text",
				readOnly: false,
				placeholder: "John Doe",
				order: 4,
				maxLength: 50,
				required: true
			},
			{
				title: "User Group",
				key: "permissionsGroup",
				order: 5,
				sortOrder: 0,
				sortOrderReversed: false,
				searchable: true,
				visible: true,
				dataType: "component",
				data: UserGroupInput
			},
			{
				title: "Area",
				key: "groupID",
				dataType: "component",
				readOnly: false,
				required: true,
				data: AreaTreeSelect,
				props: {
					reduce: node => node.id,
					clearable: false,
					appendToBody: true
				},
				order: 6
			},
			{
				title: "Address",
				key: "address",
				dataType: "text",
				readOnly: false,
				order: 7,
				maxLength: 200
			},
			{
				title: "Telephone",
				key: "telephone",
				dataType: "tel",
				readOnly: false,
				order: 8,
				maxLength: 50
			},
			{
				title: "Cell",
				key: "mobile",
				dataType: "tel",
				readOnly: false,
				order: 8,
				maxLength: 50
			},
			{
				title: "Available as Contact",
				key: "isSiteStaff",
				dataType: "checkbox",
				readOnly: false,
				visible: true,
				order: 9
			},
			{
				title: "Mobile Enabled",
				key: "mobileAppEnabled",
				dataType: "checkbox",
				readOnly: false,
				order: 10
			},
			{
				title: "Receive incident emails",
				key: "receiveIncidentEmails",
				description: "User will receive an email or push notification of any incident that occurs on the site they belong to.",
				dataType: "checkbox",
				readOnly: false,
				order: 11
			},
			{
				title: "System Admin",
				key: "isSystemAdmin",
				order: 12,
				visible: this.isSystemAdmin,
				dataType: "checkbox",
			},
			{
				title: "Email",
				key: "email",
				dataType: "email",
				readOnly: false,
				visible: !this.emailAsUser,
				order: 4,
				maxLength: 150
			},
			{
				title: "Disable Or Enable User",
				key: "disabledAt",
				order: 13,
				sortOrder: 0,
				sortOrderReversed: false,
				searchable: false,
				visible: this.canDisableUsers,
				dataType: "component",
				data: DisableUser
			},
			{
				title: "Receive setup change emails",
				key: "receiveUpdateEmails",
				dataType: "checkbox",
				description: "User will receive an email for any changes that are made to the site they belong to.",
				readOnly: false,
				order: 14
			},
		] as ModalItem[];
	}

	private async loadUserList(cancelToken?: CancelToken): Promise<UserDetails[]> {
		return await api.loadUserList(
			this.pageIndex,
			this.pageSize,
			this.searchTerm,
			cancelToken,
			this.includeBlankUsernames
		);
	}

	public get canEnableAD() {
		if (get(this.featuresList, ["Users", "CanSetActiveDirectory"]) == null) {
			return false;
		} else {
			return get(this.featuresList, ["Users", "CanSetActiveDirectory"]);
		}
	}

	public get isADChecked() {
		if (this.getModalRow != null && this.getModalRow.useAD != 0) {
			return this.getModalRow.useAD;
		} else {
			return false;
		}
	}

	public async updateUserDetails(userToUpdate: UserDetails) {
		this.setUserPermissions(userToUpdate);
		if (this.emailAsUser) {
			userToUpdate.email = userToUpdate.username;
		}
		await this.updateUser(userToUpdate);
		this.resetUserList();
		await this.updateData();
	}

	public async newUserDetails(userToAdd) {
		if (this.isADChecked) {
			delete userToAdd.useAD;
			userToAdd.password = "-";
		}

		this.setUserPermissions(userToAdd);
		if (this.emailAsUser) {
			userToAdd.email = userToAdd.username;
		}
		await this.createUser(userToAdd);
		this.resetUserList();
		await this.updateData();
	}

	public async deleteUserDetails(userToDelete: UserDetails) {
		await this.deleteUser(userToDelete.userID);
	}

	public setUserPermissions(user: UserDetails) {
		var permissions = [];
		for (var item in user.permissionsGroup) {
			if (user.permissionsGroup[item].userGroupId) {
				permissions[item] = user.permissionsGroup[item].userGroupId;
			} else if (user.permissionsGroup[item].key) {
				permissions[item] = user.permissionsGroup[item].key;
			}
		}
		user.permissionsGroup = permissions;
	}

	public get userManagementEnabled() {
		return get(this.featuresList, ["Users"]);
	}

	public renderUserGroupBadge(renderData) {
		for (var item in this.permissionsList) {
			if (renderData == this.permissionsList[item].userGroupId) {
				return this.permissionsList[item].title;
			}
		}
		return renderData;
	}

	public getRowId() {
		if (this.getModalRow && this.getModalRow.userID) {
			return this.getModalRow.userID;
		}
		return "";
	}

	public async updateData() {
		this.setUserTableIsBusy(true);
		this.setAllUsersRetrieved(false);

		let updatedUserList: UserDetails[] = await this.loadUserList(this.userSearchToken?.token);

		if (!updatedUserList || updatedUserList.length === 0) {
			this.setUserList([]);
			this.setUserTableIsBusy(false);
			return false;
		}
		this.permissionsList = await this.getUserGroup();
		for (var row in updatedUserList) {
			for (var item in updatedUserList[row].permissionsGroup) {
				updatedUserList[row].permissionsGroup[item] = {
					key: updatedUserList[row].permissionsGroup[item],
					title: this.renderUserGroupBadge(updatedUserList[row].permissionsGroup[item])
				};
			}
		}
		for (let i = updatedUserList.length - 1; i >= 0; i--) {
			if (!updatedUserList[i].username) {
				updatedUserList.splice(i, 1);
			}
		}

		updatedUserList.forEach(element => {
			if (this.currentUserId == element.userID) {
				element.readOnly = true;
			}
			if (element.permissionsGroup.length == 0) {
				element.permissionsGroup = [];
			}

			let groupTitle = this.getAreaTitle(element.groupID);
			element.groupName = groupTitle ?? "Failed to find Group";
		});

		if (!updatedUserList || this.pageSize > updatedUserList.length) {
			this.setAllUsersRetrieved(true);
		}
		this.setUserList(updatedUserList);
		this.setUserTableIsBusy(false);
		return true;
	}
	// If feature is on / off
	public get isUsersEnabled() {
		return get(this.featuresList, ["Users"]);
	}

	public get canDisableUsers() {
		return get(this.permissions, ["canEnableDisableUsers"], false);
	}

	public get isSystemAdmin() {
		return get(this.permissions, ["isAdmin"], false);
	}

	async mounted() {
		this.emailAsUser = get(this.featuresList, ["Users", "EmailAsUser"]);

		await this.fetchAreaDictionary();
		await this.updateData();
		await this.fetchLicenseDetails();
	}

	private async openModal(row, readOnly?, addNew?) {
		this.setIsLoading(true);
		this.$refs.GenericUpdateModal.showUpdateDialog(row, readOnly, addNew);
	}
	private async created(): Promise<void> {
		this.resetState();
		this.editPermissions = ["CanEditSiteSetup"];
	}

	private async handleScrollBarReachEnd(): Promise<boolean> {
		if (this.allUsersRetrieved || this.userTableIsBusy || this.loadingMoreEntries) {
			return false;
		}
		this.setPageIndex(this.pageIndex + 1);
		this.loadingMoreEntries = true;
		await this.updateData();
		this.loadingMoreEntries = false;
		return true;
	}

	private async handleSearchInput(): Promise<void> {
		this.setUserTableIsBusy(true);
		await this.performDebouncedSearch();
	}

	private performDebouncedSearch = debounce(
		async () => {
			await this.performSearch();
		},
		debouncePeriod,
		{ leading: false, trailing: true }
	);

	private async performSearch() {
		if (this.userSearchToken) {
			this.userSearchToken.cancel("Cancel Search");
		}
		this.userSearchToken = axios.CancelToken.source();
		this.resetUserList();
		try {
			await this.updateData();
		} catch (e) {
			return; //Return when past requests are cancelled
		}
		this.setUserTableIsBusy(false);
	}
	private deletePermissions() {}

	private checkPermissions(row: UserDetails, requiredPermission: string[], current: string[], readOnly: Boolean) {
		if (readOnly) {
			return false;
		}
		// if required permission is blank, we dont need a specific permission
		if (requiredPermission == null) {
			return true;
		}

		if (requiredPermission && current) {
			// look through the users permission to see if we have one that matches
			let userHasPermission = requiredPermission.some(r => {
				if (typeof current[r] == "boolean") {
					// check we have the permission set, and that it is set to true
					return Object.keys(current).includes(r) && current[r] == true;
				}

				// check we have the permission set
				return Object.keys(current).includes(r);
			});

			return userHasPermission;
		}

		return false;
	}
}
